import type { Clarification, Idea, Persona } from "../data/types";
import {
  clarificationsOf,
  critiquesOf,
  personasOf,
  scanResultOf,
} from "../data/types";

// ============================================================================
// Mom-Test clarification (step 6)
// ============================================================================

export const MOM_TEST_SYSTEM_PROMPT = `You are a thinking partner trained in Rob Fitzpatrick's Mom Test. Ask one question at a time. Never give opinions on the idea. Probe past behavior over future intentions. Use frames like: "Tell me about the last time…", "Who else has hit this problem?", "What did you try?", "What would have to be true for…". Never say "great idea" or "interesting." Be neutral, curious, and specific. After 5 to 8 useful answers, signal that clarification is complete by responding with exactly:

DONE: <one-sentence summary of what you learned about the problem, the user, and the current alternative>

Output only the next question, or the DONE: line. No preamble, no quotation marks, no commentary, no labels like "Question:".`;

export function buildClarifyUserMessage(input: {
  title: string;
  rawCapture: string;
  clarifications: Clarification[];
}): string {
  const header =
    `Idea title: ${input.title}\n` +
    `Raw capture:\n${input.rawCapture.trim() || "(no additional notes)"}`;
  if (input.clarifications.length === 0) {
    return `${header}\n\nWhat's your first clarifying question?`;
  }
  const history = input.clarifications
    .map((c, idx) => `Q${idx + 1}: ${c.question}\nA${idx + 1}: ${c.answer}`)
    .join("\n\n");
  return (
    `${header}\n\n` +
    `Clarification so far (${input.clarifications.length} answered):\n` +
    `${history}\n\n` +
    `What's your next clarifying question? If you've gathered enough to summarize the problem, target user, and current alternative, respond with the DONE: line instead.`
  );
}

export type ClarifyReply =
  | { kind: "question"; text: string }
  | { kind: "done"; summary: string };

export function parseClarifyResponse(raw: string): ClarifyReply {
  let text = raw.trim();
  const upper = text.toUpperCase();
  const idx = upper.indexOf("DONE:");
  if (idx !== -1) {
    const summary = text.slice(idx + 5).trim();
    return { kind: "done", summary };
  }
  text = text.replace(/^\s*Q\d*\s*:\s*/i, "");
  text = text.replace(/^["'“”]+|["'“”]+$/g, "").trim();
  return { kind: "question", text };
}

// ============================================================================
// Scan (step 8) — Product Hunt is gated/CORSy from the browser, so v1 uses
// the active LLM to brainstorm a list of similar / competing products. Same
// downstream shape as a real PH scan would produce.
// ============================================================================

export const SCAN_SYSTEM_PROMPT = `You are a market scout for indie hackers. Given an idea, return a JSON array of 6 to 10 real products that are direct competitors, adjacent alternatives, or analogues the user should know about — the kind of things a Product Hunt scan would surface. Real product names only; do not invent products. If unsure, omit rather than fabricate.

Return ONLY a JSON array of objects, no preamble, no code fences, no commentary. Each object has exactly these keys:
  name        — the product's real, current name
  tagline     — a one-line description of what it does
  rationale   — one sentence on why it's relevant to this idea (competitor / adjacent / analogue)`;

export function buildScanUserMessage(idea: Idea): string {
  const clar = clarificationsOf(idea);
  const summary =
    clar.length === 0
      ? ""
      : `\n\nClarification:\n${clar
          .map((c) => `- ${c.question}\n  ${c.answer}`)
          .join("\n")}`;
  return (
    `Idea title: ${idea.title}\n` +
    `Raw capture:\n${idea.rawCapture.trim() || "(no additional notes)"}` +
    summary +
    `\n\nReturn the JSON array now.`
  );
}

// ============================================================================
// Personas (step 9) — single call returns 5 personas as JSON.
// ============================================================================

export const PERSONAS_SYSTEM_PROMPT = `Generate 5 distinct, evidence-grounded personas for the indie-hacker idea described. Each must be grounded in the scan results provided — cite which scanned product or theme informed them in evidence_source.

Avoid generic demographics. Pull names from the language and context of the scan. Personas should disagree with each other where the evidence supports disagreement.

Return ONLY a JSON array of exactly 5 objects, no preamble, no code fences. Each object has exactly these keys:
  name              — a real-feeling first name; never stereotypical
  descriptor        — one-line role capturing relationship to the problem ("the burnt-out PM who tried three task managers this year")
  voice_signature   — 1–2 lines describing how they talk; used to modify each critique prompt so personas read distinguishably
  evidence_source   — which scanned product / theme grounds them ("Derived from comments on <product>")
  jobs_to_be_done   — one sentence
  current_alternative — what they use today
  dealbreakers      — array of 2–4 short strings
  must_haves        — array of 2–4 short strings`;

export function buildPersonasUserMessage(idea: Idea): string {
  const clar = clarificationsOf(idea);
  const scan = scanResultOf(idea);
  const clarStr =
    clar.length === 0
      ? "(no clarifications captured)"
      : clar.map((c, i) => `Q${i + 1}: ${c.question}\nA${i + 1}: ${c.answer}`).join("\n\n");
  const scanStr =
    !scan || scan.products.length === 0
      ? "(no scan results)"
      : scan.products
          .map((p) => `- ${p.name}: ${p.tagline} (${p.rationale})`)
          .join("\n");
  return (
    `Idea title: ${idea.title}\n` +
    `Raw capture:\n${idea.rawCapture.trim() || "(no notes)"}\n\n` +
    `Clarification:\n${clarStr}\n\n` +
    `Scan results:\n${scanStr}\n\n` +
    `Return the JSON array of 5 personas now.`
  );
}

// ============================================================================
// Critique (step 10) — one call per persona, with the persona's voice
// signature baked into the prompt so each reaction reads distinctly.
// ============================================================================

export function buildCritiqueSystemPrompt(persona: Persona): string {
  return `You are ${persona.name}, ${persona.descriptor}.
Your voice: ${persona.voiceSignature}

React to the idea as yourself. Mom-Test discipline: describe what you currently do about this problem, what you've tried, what would make you switch from your current alternative, what would be a dealbreaker. Speak in the voice described above — your text should be distinguishable from the other personas. Do not give product feedback or opinions on whether it's a "good idea." Speak from a life, not from the air.

Return ONLY a JSON object with these exact keys:
  problem_fit_score        — integer 1 to 5, how acute this problem is for you
  switch_likelihood_score  — integer 1 to 5, how likely you'd actually switch from your current alternative
  reaction_summary         — 2–4 sentences in your voice
  questions_back           — array of 1–3 Mom-Test-style questions you'd ask the founder
  dealbreakers             — array of 1–3 short strings
  must_haves               — array of 1–3 short strings

No preamble, no code fences, no commentary outside the JSON.`;
}

export function buildCritiqueUserMessage(idea: Idea, persona: Persona): string {
  const clar = clarificationsOf(idea);
  const clarStr =
    clar.length === 0
      ? ""
      : `\n\nWhat the founder told a coach:\n${clar
          .map((c) => `Q: ${c.question}\nA: ${c.answer}`)
          .join("\n\n")}`;
  return (
    `Idea title: ${idea.title}\n` +
    `Raw capture:\n${idea.rawCapture.trim() || "(no notes)"}` +
    clarStr +
    `\n\nYour current alternative: ${persona.currentAlternative}` +
    `\nYour jobs-to-be-done: ${persona.jobsToBeDone}` +
    `\n\nReact now as ${persona.name}. Return the JSON object.`
  );
}

// ============================================================================
// Build spec (step 11) — single synthesis call that returns Markdown.
// ============================================================================

export const BUILD_SPEC_SYSTEM_PROMPT = `Synthesize a paste-ready build spec for the idea below. The output must be a Markdown document a developer can paste directly into Cursor, Claude Code, Lovable, or Bolt to start scaffolding within minutes.

Every key feature in v1 MUST cite which persona's must-have motivated it (by name). Every non-goal MUST cite which persona's dealbreaker motivated it (by name). Tech-stack suggestions bias toward vibe-coder-friendly choices (Next.js + Supabase, Flutter + Firebase, native mobile with managed backends) rather than enterprise patterns. The "validation steps" section must include a concrete Mom-Test interview script — specific past-behavior questions tailored to the personas.

No generic startup wisdom. No "it depends." No verdict on whether the idea is good — assume the user has decided to build, and your job is to make the version they build sharper than the version they walked in with.

Output a single Markdown document with these top-level sections, in this order:
  # <Product name suggestion>
  ## Product summary
  ## Target user
  ## Positioning
  ## Core principles
  ## Key features (v1)         — bullets; each cites (persona_name) at the end
  ## Data model outline        — plain-English schema
  ## Tech stack suggestions
  ## v1 scope
  ## Deferred (v2+)
  ## Non-goals                  — bullets; each cites (persona_name) at the end
  ## Build order                — first 5–10 ordered steps
  ## Open risks
  ## Validation steps          — includes Mom-Test interview script

Return ONLY the Markdown. No preamble, no code fences around the whole thing, no commentary.`;

export function buildSpecUserMessage(idea: Idea): string {
  const clar = clarificationsOf(idea);
  const personas = personasOf(idea);
  const critiques = critiquesOf(idea);
  const scan = scanResultOf(idea);

  const clarStr =
    clar.length === 0
      ? "(no clarifications)"
      : clar.map((c, i) => `Q${i + 1}: ${c.question}\nA${i + 1}: ${c.answer}`).join("\n\n");

  const scanStr =
    !scan || scan.products.length === 0
      ? "(no scan)"
      : scan.products.map((p) => `- ${p.name}: ${p.tagline} (${p.rationale})`).join("\n");

  const personasStr =
    personas.length === 0
      ? "(no personas)"
      : personas
          .map(
            (p) =>
              `- ${p.name} (${p.descriptor})\n` +
              `  jobs: ${p.jobsToBeDone}\n` +
              `  current alternative: ${p.currentAlternative}\n` +
              `  must-haves: ${p.mustHaves.join("; ")}\n` +
              `  dealbreakers: ${p.dealbreakers.join("; ")}`,
          )
          .join("\n\n");

  const critiquesStr =
    critiques.length === 0
      ? "(no critiques)"
      : critiques
          .map((c) => {
            const persona = personas.find((p) => p.id === c.personaId);
            const name = persona?.name ?? c.personaId;
            return (
              `${name}: problem-fit ${c.problemFitScore}/5, switch-likelihood ${c.switchLikelihoodScore}/5\n` +
              `  reaction: ${c.reactionSummary}\n` +
              `  must-haves: ${c.mustHaves.join("; ")}\n` +
              `  dealbreakers: ${c.dealbreakers.join("; ")}\n` +
              `  questions back: ${c.questionsBack.join("; ")}`
            );
          })
          .join("\n\n");

  return (
    `Idea title: ${idea.title}\n\n` +
    `Raw capture:\n${idea.rawCapture.trim() || "(no notes)"}\n\n` +
    `Clarifications:\n${clarStr}\n\n` +
    `Scan results:\n${scanStr}\n\n` +
    `Personas:\n${personasStr}\n\n` +
    `Critiques:\n${critiquesStr}\n\n` +
    `Generate the Markdown build spec now.`
  );
}

// ============================================================================
// Shared JSON extraction. LLMs occasionally wrap JSON in ```json fences or
// add a "Here's the JSON:" preamble; this strips both and returns the
// first parseable object/array.
// ============================================================================

export function extractFirstJson<T = unknown>(raw: string): T | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  // Strip code fences if present
  const fenced = trimmed.match(/```(?:json)?\s*([\s\S]*?)```/);
  const candidate = fenced ? fenced[1].trim() : trimmed;
  // Find first { or [ and last } or ] — outermost JSON span
  const start = Math.min(
    candidate.indexOf("{") === -1 ? Number.MAX_SAFE_INTEGER : candidate.indexOf("{"),
    candidate.indexOf("[") === -1 ? Number.MAX_SAFE_INTEGER : candidate.indexOf("["),
  );
  if (!Number.isFinite(start)) return null;
  const lastObj = candidate.lastIndexOf("}");
  const lastArr = candidate.lastIndexOf("]");
  const end = Math.max(lastObj, lastArr);
  if (end < start) return null;
  const slice = candidate.slice(start, end + 1);
  try {
    return JSON.parse(slice) as T;
  } catch {
    return null;
  }
}
